import React from "react";
import { Button, Typography, Paper, Chip } from "@mui/material";
import { JobAttribute } from "./JobAttribute";
import PlaceIcon from "@mui/icons-material/Place";
import PaymentsIcon from "@mui/icons-material/Payments";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Job } from "../../types";

interface JobSuggestionProps {
  job: Job;
  currentJobIndex: number;
  totalJobs: number;
  onJobPreference: (job: Job, interested: boolean) => void;
  onComplete: () => void;
  onJobViewed?: (job: Job) => Job | null;
  countryCode?: string;
}

const currencySymbolMap: { [key: string]: string } = {
  "EUR": "€",
  "USD": "$",
  "PLN": "zł",
  "GBP": "£",
  "JPY": "¥",
  "AUD": "A$",
  "CAD": "C$",
  "CHF": "Fr",
  "CNY": "¥",
  "SEK": "kr",
  "NZD": "NZ$",
  "MXN": "$",
  "SGD": "S$",
  "HKD": "HK$",
  "NOK": "kr",
  "KRW": "₩",
  "TRY": "₺",
  "RUB": "₽",
  "INR": "₹",
  "BRL": "R$",
  "ZAR": "R",
  "AED": "د.إ",
  "SAR": "﷼",
  "QAR": "﷼",
  "EGP": "£",
  "KES": "KSh",
  "NGN": "₦",
  "GHS": "₵",
  "XOF": "CFA",
  "XAF": "FCFA",
  "DKK": "kr",
  "HUF": "Ft",
  "CZK": "Kč",
  "ILS": "₪",
  "CLP": "$",
  "COP": "$",
  "ARS": "$",
  "PEN": "S/",
  "IDR": "Rp",
  "MYR": "RM",
  "PHP": "₱",
  "THB": "฿",
  "VND": "₫",
  "PKR": "₨",
  "BDT": "৳",
  "NPR": "₨",
  "LKR": "₨",
};

const getCurrencySymbol = (code: string | undefined): string => {
  if (!code) {
    return "";
  }
  return currencySymbolMap[code.toUpperCase()] || "";
};

const formatLocation = (location: Job["location"]) => {
  if (!location) return "N/A";
  const parts = [location.city, location.country, location.remote_option]
    .filter(Boolean)
    .join(", ");
  return parts || "N/A";
};

const formatSalary = (salary: Job["salary"]) => {
  if (!salary) return "N/A";
  const { min, max, value, salary_period, currency } = salary;
  const symbol = getCurrencySymbol(currency);

  if (value) return `${symbol}${value} ${salary_period}`;
  if (min && max) return `${symbol}${min} - ${symbol}${max} ${salary_period}`;
  if (min) return `From ${symbol}${min} ${salary_period}`;
  if (max) return `Up to ${symbol}${max} ${salary_period}`;
  return "N/A";
};

const formatContractType = (contract_type: Job["contract_type"]) => {
  if (!contract_type) return "N/A";
  const {
    type,
    temporary,
    min_hours_per_week,
    max_hours_per_week,
    hours_per_week,
  } = contract_type;
  let result = type || "";
  if (temporary) result += " (Temporary)";
  if (hours_per_week) result += `, ${hours_per_week}h/week`;
  else if (min_hours_per_week && max_hours_per_week)
    result += `, ${min_hours_per_week}-${max_hours_per_week}h/week`;
  else if (min_hours_per_week) result += `, from ${min_hours_per_week}h/week`;
  else if (max_hours_per_week) result += `, up to ${max_hours_per_week}h/week`;
  return result || "N/A";
};

export const JobSuggestion: React.FC<JobSuggestionProps> = ({
  job,
  currentJobIndex,
  totalJobs,
  onJobPreference,
  onComplete,
  onJobViewed,
  countryCode,
}) => {
  const handlePreference = (interested: boolean) => {
    onJobPreference(job, interested);

    setTimeout(() => {
      if (onJobViewed) {
        onJobViewed(job);
      } else {
        if (currentJobIndex < totalJobs - 1) {
        } else {
          onComplete();
        }
      }
    }, 500);
  };

  return (
    <Paper
      elevation={8}
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 1, sm: 1.5 },
        height: "100%",
        maxHeight: { xs: "75vh", sm: "70vh" },
        width: "100%",
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: { xs: "16px", sm: "20px" },
        border: "1px solid rgba(255, 255, 255, 0.2)",
      }}
    >
      {/* Progress indicator */}
      <div style={{ textAlign: "center", marginBottom: "8px" }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: { xs: "13px", sm: "14px" } }}
        >
          Job {currentJobIndex + 1} of {totalJobs}
        </Typography>
      </div>

      {/* Scrollable content */}
      <div
        style={{
          overflowY: "auto",
          flexGrow: 1,
          paddingRight: "12px",
        }}
      >
        {/* Job title and company */}
        <div style={{ marginBottom: "16px" }}>
          <Typography
            variant="h6"
            sx={{
              color: "#003366",
              fontWeight: "bold",
              fontSize: { xs: "18px", sm: "20px", md: "22px" },
              lineHeight: 1.3,
              mb: 0.5,
            }}
          >
            {job.title}
          </Typography>
          {job.company && (
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ fontSize: { xs: "10px", sm: "13px" } }}
            >
              {job.company}
            </Typography>
          )}
        </div>

        {/* Job details */}
        <div style={{ marginBottom: "16px" }}>
          <JobAttribute
            IconComponent={PlaceIcon}
            text={formatLocation(job.location)}
          />
          <JobAttribute
            IconComponent={PaymentsIcon}
            text={formatSalary(job.salary)}
          />
          <JobAttribute
            IconComponent={AccessTimeIcon}
            text={formatContractType(job.contract_type)}
          />
        </div>

        {/* Description */}
        {(job.brief_description || job.description) && (
          <Typography
            variant="body1"
            sx={{
              my: { xs: 1.5, sm: 2 },
              color: "#333",
              fontSize: { xs: "14px", sm: "15px" },
              lineHeight: 1.6,
            }}
          >
            {job.brief_description || job.description}
          </Typography>
        )}

        {/* Must-haves */}
        {job.hard_requirements && job.hard_requirements.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                mb: 1,
                fontSize: { xs: "14px", sm: "15px" },
              }}
            >
              Must-Haves:
            </Typography>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {job.hard_requirements.map((req, index) => (
                <Chip
                  key={index}
                  label={req}
                  size="small"
                  sx={{
                    fontSize: { xs: "12px", sm: "13px" },
                    height: { xs: "26px", sm: "28px" },
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Nice-to-haves */}
        {job.soft_requirements && job.soft_requirements.length > 0 && (
          <div style={{ marginBottom: "12px" }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: "bold",
                mb: 1,
                fontSize: { xs: "14px", sm: "15px" },
              }}
            >
              Nice-to-Haves:
            </Typography>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {job.soft_requirements.map((req, index) => (
                <Chip
                  key={index}
                  label={req}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: { xs: "12px", sm: "13px" },
                    height: { xs: "26px", sm: "28px" },
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          gap: "16px",
          paddingTop: "16px",
          borderTop: "1px solid #eee",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          color="error"
          onClick={() => handlePreference(false)}
          sx={{
            width: { xs: "100%", sm: "150px" },
            height: { xs: "48px", sm: "50px" },
            fontSize: { xs: "15px", sm: "16px" },
            fontWeight: 500,
            borderRadius: "8px",
            textTransform: "none",
          }}
        >
          Not Interested
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handlePreference(true)}
          sx={{
            width: { xs: "100%", sm: "150px" },
            height: { xs: "48px", sm: "50px" },
            fontSize: { xs: "15px", sm: "16px" },
            fontWeight: 600,
            borderRadius: "8px",
            textTransform: "none",
          }}
        >
          Interested
        </Button>
      </div>
    </Paper>
  );
};
